import { motion, MotionConfig } from "framer-motion";
import { AlertCircle, Database, Users } from "lucide-react";
import { useInViewOnce } from "../../../hooks/useInViewOnce";

const SPRING: [number, number, number, number] = [0.34, 1.56, 0.64, 1];

const PROBLEMS = [
  {
    icon: Database,
    color: "from-rose-500/20 to-red-500/10 border-rose-400/30",
    iconColor: "from-rose-400 to-red-500",
    title: "Dados dispersos em planilhas",
    description:
      "Notas no Excel, chamadas no caderno, comunicados no WhatsApp. Ninguém enxerga o mesmo aluno — e a família só descobre problemas quando a prova já passou.",
  },
  {
    icon: Users,
    color: "from-amber-500/20 to-orange-500/10 border-amber-400/30",
    iconColor: "from-amber-400 to-orange-500",
    title: "Coordenação no escuro",
    description:
      "Sem visibilidade por turma, disciplina ou professor, decisões pedagógicas viram achismo. Reuniões pedem dados que ninguém tem ou que chegam desatualizados.",
  },
  {
    icon: AlertCircle,
    color: "from-violet-500/20 to-purple-500/10 border-violet-400/30",
    iconColor: "from-violet-400 to-purple-500",
    title: "Ensino padronizado para todos",
    description:
      "O mesmo conteúdo, no mesmo ritmo, para alunos com perfis completamente diferentes. Quem aprende diferente fica para trás — e o professor não tem como atender um por um.",
  },
];

const itemVariants = {
  hidden: { opacity: 0, y: 36 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.65, ease: SPRING, delay: i * 0.1 },
  }),
};

export function ProblemSection() {
  const { ref, isVisible } = useInViewOnce();

  return (
    <MotionConfig reducedMotion="user">
      <section
        ref={ref}
        className="border-b border-white/[0.06] py-16 sm:py-20 lg:py-24"
      >
        <div className="container mx-auto max-w-7xl px-4 sm:px-6">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={isVisible ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, ease: [0, 0, 0.2, 1] }}
            className="mx-auto max-w-2xl text-center"
          >
            <span className="mb-4 inline-block rounded-full border border-red-500/30 bg-red-500/10 px-3 py-1 text-xs font-medium text-red-300 sm:text-sm">
              O problema que a escola tem hoje
            </span>
            <h2 className="font-display text-2xl font-bold tracking-tight text-white sm:text-3xl md:text-4xl">
              Gestão fragmentada custa{" "}
              <span className="bg-gradient-to-r from-rose-300 to-amber-300 bg-clip-text text-transparent">
                aprendizado e tempo
              </span>
            </h2>
            <p className="mt-4 text-base leading-relaxed text-slate-400 sm:text-lg">
              Antes de falar de IA, a escola precisa de dados organizados. Sem uma
              base sólida de gestão, a personalização não parte do lugar.
            </p>
          </motion.div>

          <div className="mx-auto mt-12 grid max-w-6xl gap-5 sm:mt-16 md:grid-cols-3">
            {PROBLEMS.map((p, i) => (
              <motion.div
                key={p.title}
                custom={i}
                variants={itemVariants}
                initial="hidden"
                animate={isVisible ? "visible" : "hidden"}
                className={`card-lift flex flex-col gap-4 rounded-2xl border bg-gradient-to-br p-6 ${p.color}`}
              >
                <div
                  className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br shadow-lg ${p.iconColor}`}
                >
                  <p.icon className="h-6 w-6 text-white" aria-hidden />
                </div>
                <div>
                  <h3 className="font-display text-lg font-semibold text-white">
                    {p.title}
                  </h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-300">
                    {p.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>

          <motion.p
            initial={{ opacity: 0 }}
            animate={isVisible ? { opacity: 1 } : {}}
            transition={{ delay: 0.5, duration: 0.5 }}
            className="mt-10 text-center text-sm text-teal-400/80 sm:mt-12"
          >
            O NeuroMent resolve a gestão primeiro — e adiciona a inteligência pedagógica em cima.
          </motion.p>
        </div>
      </section>
    </MotionConfig>
  );
}
